import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { auth } from '../../../lib/firebase';
import { Cable, Plus, RefreshCw, Download, Inbox, CheckCircle2, XCircle } from 'lucide-react';
import { tenantHeaders } from '../../../lib/tenantSession';

// Firebase ID token first (staff log in via Firebase; the token is NOT in localStorage),
// falling back to a stored OTP/JWT token. Using only localStorage previously caused
// IT/Integration (Firebase-auth) to send an empty token → 401 → hub failed to load.
const getToken = async () =>
  (auth.currentUser ? await auth.currentUser.getIdToken() : null) || localStorage.getItem('crm_access_token') || '';

// B6A (2026-06-18): Tenant Business-Plane Integration Hub. Shows ONLY this tenant's
// configured connectors from the hardened, redacted GET /tenants/{id}/integrations.
// No fake metrics/integrations/logs. Platform-level governance controls are
// Owner/SuperAdmin Plane and are not present here. Connector sync/test/webhook
// management have no tenant backend yet and are shown as honest PENDING states,
// never as fake-success buttons.
const CONNECTOR_LABELS = { whatsapp: 'WhatsApp', email: 'Email', website: 'Website' };

const IntegrationHub = () => {
  const navigate = useNavigate();
  const { currentBusiness } = useBusiness();
  const { t } = useLanguage();
  const brandId = currentBusiness?.brandId;
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState({});
  const [tier, setTier] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!brandId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/tenants/${brandId}/integrations`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      if (!res.ok) throw new Error(t('st.hub.errLoad', { status: res.status }));
      const data = await res.json();
      setIntegrations(data.integrations || {});
      setTier(data.tier || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  const configured = Object.entries(integrations).filter(([, c]) => c && typeof c === 'object');
  const active = configured.filter(([, c]) => c.enabled);

  const exportData = () => {
    const rows = [['connector', 'enabled'], ...configured.map(([k, c]) => [k, !!c.enabled])];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `integrations-${brandId || 'tenant'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Cable className="h-5 w-5" /> {t('nav.integrationHub')}
          </h1>
          <p className="text-gray-600">{t('st.hub.subtitle')}{tier ? ` · ${t('st.hub.planTag', { tier })}` : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />{t('st.hub.refreshStatus')}</Button>
          <Button variant="outline" onClick={exportData}><Download className="h-4 w-4 mr-1" />{t('st.hub.exportData')}</Button>
          <Button onClick={() => navigate('/business/settings?tab=integrations')}><Plus className="h-4 w-4 mr-1" />{t('st.hub.addIntegration')}</Button>
        </div>
      </div>

      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        {t('st.hub.scopeNote')}
      </div>

      {loading ? (
        <Card><CardContent className="py-10 text-center text-gray-500">{t('common.loading')}</CardContent></Card>
      ) : error ? (
        <Card><CardContent className="py-10 text-center text-red-600">{error}</CardContent></Card>
      ) : active.length === 0 ? (
        <Card>
          <CardContent className="py-12 flex flex-col items-center text-center text-gray-500">
            <Inbox className="h-10 w-10 mb-3" />
            <p className="font-medium">{t('st.hub.none')}</p>
            <Button className="mt-4" onClick={() => navigate('/business/settings?tab=integrations')}>
              {t('st.hub.configureInSettings')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {active.map(([key, cfg]) => (
            <Card key={key}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">{CONNECTOR_LABELS[key] || key}</CardTitle>
                <Badge className={cfg.enabled ? 'bg-green-500' : ''} variant={cfg.enabled ? undefined : 'secondary'}>
                  {cfg.enabled
                    ? (<><CheckCircle2 className="h-3 w-3 mr-1" />{t('st.enabled')}</>)
                    : (<><XCircle className="h-3 w-3 mr-1" />{t('st.disabled')}</>)}
                </Badge>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500">
                  {t('st.hub.connectorNote')}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {t('st.hub.syncNote')}
                </p>
                <Button variant="outline" className="mt-3" onClick={() => navigate('/business/settings?tab=integrations')}>
                  {t('st.hub.configure')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="mt-4">
        <CardHeader><CardTitle className="text-base">{t('st.hub.webhooks')}</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">{t('st.hub.webhooksNote')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationHub;
